/**
 * Conflicts API — the airport security scanner.
 *
 * When a clerk types party names during matter creation, this calls
 * the backend which scans ALL existing matters in PostgreSQL for matches.
 * No more relying on browser localStorage — this checks the real database.
 */
import { api } from './api';

export interface ConflictScanResult {
  severity: 'critical' | 'warning' | 'info';
  matchMethod: 'party_name' | 'contact_id' | 'solicitor_firm' | 'barrister_conflict';
  existingMatterId: string;
  existingMatterName: string;
  existingMatterNumber: string;
  existingBarrister: string | null;
  reason: string;
}

export interface ScanResponse {
  conflicts: ConflictScanResult[];
  hasConflicts: boolean;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
}

/**
 * Scan for conflicts when creating a new matter.
 * Called as the clerk types party names — debounce on the frontend.
 */
export async function scanForConflicts(
  parties: { name: string; role: string; contactId?: string; solicitorFirm?: string; phone?: string; altPhone?: string }[],
  barristerId?: string,
  excludeMatterId?: string,
  clerkId?: string,
): Promise<ScanResponse> {
  const res = await api.post('/conflicts/scan', {
    parties,
    barristerId,
    excludeMatterId,
    clerkId,
  });
  return (res.data as any).data ?? res.data;
}

/**
 * Re-run the full conflict scan for an EXISTING matter from its current parties.
 *
 * This is the only path that raises a mid-matter conflict review: a critical result on a live
 * engagement suspends it and opens an InstructionConflictReview for an authorised decision
 * (conflicts.service recheckMatter → raiseConflictReview). The endpoint existed with no caller,
 * so the Conflict Reviews queue could never receive anything through the product.
 */
export async function recheckConflicts(caseId: string): Promise<ScanResponse> {
  const res = await api.post(`/conflicts/${caseId}/recheck`, {});
  return (res.data as any).data ?? res.data;
}

/**
 * Check if assigning a specific barrister creates a conflict.
 */
export async function checkBarristerConflict(
  barristerId: string,
  parties?: { name: string; role: string }[],
  matterId?: string,
): Promise<{ conflicts: ConflictScanResult[]; hasConflicts: boolean; blocked: boolean }> {
  const res = await api.post('/conflicts/check-barrister', {
    barristerId,
    parties,
    matterId,
  });
  return (res.data as any).data ?? res.data;
}
